import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_THEME,
  THEMES,
  THEME_STORAGE_KEY,
  isThemeId,
  type Theme,
  type ThemeId,
} from './themes';

interface IThemeContext {
  theme: Theme;
  setTheme: (id: ThemeId) => void;
}

const ThemeContext = createContext<IThemeContext | undefined>(undefined);

const readInitial = (): ThemeId => {
  try {
    const fromQuery = new URLSearchParams(globalThis.location?.search ?? '').get('theme');
    if (isThemeId(fromQuery)) {
      return fromQuery;
    }
    const stored = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
    return isThemeId(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

const apply = (theme: Theme) => {
  const root = document.documentElement;
  root.dataset.theme = theme.id;
  root.style.colorScheme = theme.dark ? 'dark' : 'light';
  Object.entries(theme.vars).forEach(([key, value]) => root.style.setProperty(key, value));
  if (!theme.vars['--bg-pattern-size']) {
    root.style.setProperty('--bg-pattern-size', 'auto');
  }
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [id, setId] = useState<ThemeId>(readInitial);
  const theme = useMemo(() => THEMES.find((item) => item.id === id) ?? THEMES[0], [id]);

  useEffect(() => apply(theme), [theme]);

  const setTheme = useCallback((next: ThemeId) => {
    setId(next);
    try {
      globalThis.localStorage?.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // storage unavailable
    }
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): IThemeContext => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }
  return context;
};
